import React, { useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Button } from "@mui/material";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import {
  cpaGold,
  cpaGoldDark,
} from "../EventSales/eventSalesShared";
import { WizardActionButton } from "../CreateEvent/wizard/wizardShared";

export type DashboardPixelQaState =
  | "create-step-1"
  | "create-step-1-copy"
  | "create-speakers"
  | "create-questions"
  | "event-code-popups"
  | "event-title-popups"
  | "event-type-popups"
  | "create-step-2"
  | "create-step-2-format"
  | "add-ticket-popup"
  | "create-step-3"
  | "create-step-3-table"
  | "edit-ticket-popup"
  | "create-step-4"
  | "sponsor-level-popup"
  | "manage-events"
  | "manage-actions"
  | "ticket-sales"
  | "ticket-sales-table"
  | "ticket-invoice"
  | "ticket-payment"
  | "ticket-payment-confirm"
  | "ticket-payment-success"
  | "sponsorship-sales"
  | "sponsorship-sales-table"
  | "sponsor-invoice"
  | "sponsor-payment"
  | "sponsor-payment-confirm"
  | "sponsor-payment-success";

export const getDashboardPixelQaState = (
  search: string,
  fallback: DashboardPixelQaState
): DashboardPixelQaState | null => {
  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  const params = new URLSearchParams(search);
  const value = params.get("pixelQaState") || params.get("pixelQa");
  if (!value) {
    return null;
  }

  const aliases: Record<string, DashboardPixelQaState> = {
    "1": fallback,
    true: fallback,
    header: "create-step-1",
    "create-event-1": "create-step-1",
    "create-event-2": "create-step-1-copy",
    "create-event-3": "create-speakers",
    "create-event-4": "create-questions",
    "create-event-5": "create-step-2",
    "create-event-6": "create-step-2-format",
    "create-event-7": "create-step-3",
    "create-event-8": "create-step-3-table",
    "create-event-9": "create-step-4",
    "add-ticket": "add-ticket-popup",
    "edit-ticket": "edit-ticket-popup",
    "sponsor-level": "sponsor-level-popup",
    manage: "manage-events",
    "ticket-sales-1": "ticket-sales",
    "ticket-sales-2": "ticket-sales-table",
    "event-registration-payment-confirm": "ticket-payment-confirm",
    "event-registration-payment-success": "ticket-payment-success",
    "sponsorship-sales-1": "sponsorship-sales",
    "sponsorship-sales-2": "sponsorship-sales-table",
    "become-a-sponsor-payment-confirm": "sponsor-payment-confirm",
    "become-a-sponsor-payment-success": "sponsor-payment-success",
  };

  return aliases[value] || (value as DashboardPixelQaState);
};

const stepLabels = [
  ["1", "Event Details", "Basic information"],
  ["2", "Date & Location", "When and where"],
  ["3", "Ticket - Set Up", "Ticket types and pricing"],
  ["4", "Sponsor Set Up", "Sponsors and packages"],
  ["", "Your Event Planner", "Coming Soon"],
];

const activeStepByState: Partial<Record<DashboardPixelQaState, number>> = {
  "create-step-1": 1,
  "create-step-1-copy": 1,
  "create-speakers": 1,
  "create-questions": 1,
  "event-code-popups": 1,
  "event-title-popups": 1,
  "event-type-popups": 1,
  "create-step-2": 2,
  "create-step-2-format": 2,
  "add-ticket-popup": 3,
  "create-step-3": 3,
  "create-step-3-table": 3,
  "edit-ticket-popup": 3,
  "create-step-4": 4,
  "sponsor-level-popup": 4,
};

const createTitleByStep: Record<number, string> = {
  1: "Create Event - Event Details",
  2: "Create Event - Date & Location",
  3: "Create Event - Ticket - Set Up",
  4: "Create Event - Sponsor Set Up",
};

const createDescriptionByStep: Record<number, string> = {
  1: "To create an event, follow the four simple steps. Starting with Event Details.",
  2: "To create an event, follow the next step and set Date & Location.",
  3: "To create an event, follow the next steps and set Ticket - Set up.",
  4: "To create an event, follow the next steps and set Sponsor Set up",
};

const Field = ({
  label,
  value,
  wide = false,
}: {
  label: string;
  value?: string;
  wide?: boolean;
}) => (
  <label className={wide ? "col-span-2" : ""}>
    <span className="mb-2 block text-sm font-extrabold text-gray-950">{label}</span>
    <div className="flex min-h-[48px] items-center rounded border border-gray-300 bg-white px-3 text-sm text-gray-600">
      {value || `Enter ${label.replace(" *", "")}`}
    </div>
  </label>
);

const GoldButton = ({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <Button
    variant="contained"
    startIcon={icon}
    sx={{
      backgroundColor: cpaGold,
      borderRadius: "6px",
      boxShadow: "none",
      color: "white",
      fontSize: 13,
      fontWeight: 800,
      minHeight: 38,
      px: 2.2,
      textTransform: "none",
      "&:hover": { backgroundColor: cpaGoldDark, boxShadow: "none" },
    }}
  >
    {children}
  </Button>
);

const RedButton = ({ children }: { children: React.ReactNode }) => (
  <button className="rounded bg-[#f2303d] px-3 py-1.5 text-xs font-bold text-white">
    {children}
  </button>
);

const BlackButton = ({ children }: { children: React.ReactNode }) => (
  <button className="rounded bg-gray-950 px-5 py-2 text-xs font-bold text-white">
    {children}
  </button>
);

const Pill = ({
  children,
  tone = "gold",
}: {
  children: React.ReactNode;
  tone?: "gold" | "green" | "blue" | "red" | "gray";
}) => {
  const tones = {
    gold: "bg-[#fff4d8] text-[#8a6418]",
    green: "bg-green-100 text-green-800",
    blue: "bg-blue-100 text-blue-800",
    red: "bg-red-100 text-red-800",
    gray: "bg-gray-100 text-gray-700",
  };
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-extrabold ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Stepper = ({ activeStep }: { activeStep: number }) => (
  <div className="-mx-8 mb-8 flex items-stretch border-y border-gray-200 bg-white px-8">
    {stepLabels.map(([number, title, subtitle], index) => {
      const stepNumber = index + 1;
      const active = stepNumber === activeStep;
      return (
        <React.Fragment key={title}>
          <div className="relative flex min-w-[185px] items-center gap-3 py-6 pr-5">
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lg font-extrabold ${
                active ? "bg-[#E1A731] text-white" : "bg-gray-100 text-black"
              }`}
            >
              {number}
            </span>
            <span>
              <span className="block text-[15px] font-extrabold leading-5 text-black">
                {title}
              </span>
              <span className="mt-1 block text-xs leading-4 text-black">
                {subtitle}
              </span>
            </span>
            {active ? (
              <span className="absolute bottom-0 left-0 h-1.5 w-full bg-[#E1A731]" />
            ) : null}
          </div>
          {index < stepLabels.length - 1 ? (
            <span className="flex items-center pr-5 text-3xl text-black">›</span>
          ) : null}
        </React.Fragment>
      );
    })}
  </div>
);

const Card = ({
  title,
  description,
  children,
  className = "",
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={`rounded border border-gray-200 bg-white p-6 shadow-sm ${className}`}>
    <h2 className="text-xl font-extrabold text-gray-950">{title}</h2>
    {description ? (
      <p className="mt-1 text-sm leading-6 text-gray-600">{description}</p>
    ) : null}
    <div className="mt-6">{children}</div>
  </section>
);

const RichBox = ({ value }: { value: string }) => (
  <div className="rounded border border-gray-200 bg-white">
    <div className="border-b border-gray-100 px-3 py-2 text-xs text-gray-500">
      Paragraph &nbsp; | &nbsp; B / I / U &nbsp; | &nbsp; Align &nbsp; | &nbsp; Link
    </div>
    <div className="min-h-[92px] px-4 py-3 text-sm leading-6 text-gray-700">{value}</div>
    <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
      0/500 characters
    </div>
  </div>
);

const UploadBox = () => (
  <div className="flex min-h-[150px] flex-col items-center justify-center rounded border border-dashed border-[#E1A731] bg-[#fffdf7] text-center">
    <div className="text-3xl text-[#E1A731]">⇧</div>
    <p className="mt-2 text-sm font-extrabold text-gray-950">Drag and drop your image here</p>
    <p className="mt-1 text-xs text-gray-500">or click to browse</p>
    <p className="mt-2 text-xs text-gray-500">Recommended size: 1440 x 720 px</p>
  </div>
);

const CreateStepOne = ({ variant }: { variant: DashboardPixelQaState }) => {
  if (variant === "create-step-1-copy") {
    return (
      <div className="grid gap-7">
        <Card title="Event Details" description="Add the event title and event type, then describe the event.">
          <div className="space-y-6">
            <RichBox value="Connect & Network" />
            <RichBox value="Celebrate the achievements of local businesses, entrepreneurs, and community leaders during an unforgettable evening of networking, recognition, entertainment, and inspiration." />
          </div>
        </Card>
        <Card title="Add Keynote Speaker or Create Speaker Panel">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Speaker Name *" value="Jane Smith" />
            <Field label="Company Name" value="CEO, Innovation Canada" />
            <Field label="Title" value="Keynote Speaker" />
          </div>
          <div className="mt-4 grid grid-cols-[220px_1fr] gap-5">
            <UploadBox />
            <RichBox value="Premier bio: Our keynote speaker will share practical insight and industry perspective." />
          </div>
          <div className="mt-4">
            <GoldButton>Save Speaker Profile</GoldButton>
          </div>
        </Card>
      </div>
    );
  }

  if (variant === "create-speakers") {
    return (
      <div className="grid gap-7">
        <Card title="Speakers List" description="Manage speaker profiles and arrange the order they appear on the event registration page.">
          <PixelTable
            columns={["Speaker Image", "Speaker Name", "Company Name", "Title", "Bio", "Actions"]}
            rows={[
              ["👩", "Enter Name", "Enter Company", "Enter Title", "Enter Bio", "Edit  Delete"],
              ["👨", "Enter Name", "Enter Company", "Enter Title", "Enter Bio", "Edit  Delete"],
              ["👩", "Enter Name", "Enter Company", "Enter Title", "Enter Bio", "Edit  Delete"],
              ["👨", "Enter Name", "Enter Company", "Enter Title", "Enter Bio", "Edit  Delete"],
            ]}
          />
        </Card>
        <div className="grid grid-cols-2 gap-8">
          <Card title="Event Visibility *">
            <RadioLine checked label="Public" helper="Anyone can view and register" />
            <RadioLine label="Private" helper="Only people with the link can view and register" />
          </Card>
          <Card title="Event Dress Code">
            <RadioLine label="Formal" />
            <RadioLine checked label="Business Attire" />
            <RadioLine label="Casual" />
          </Card>
        </div>
      </div>
    );
  }

  if (variant === "create-questions") {
    return <QuestionComposer />;
  }

  return (
    <Card title="Event Details" description="Add the event details about your event. This information will appear on your website on the event calendar and event page form.">
      <div className="grid grid-cols-2 gap-6">
        <Field label="Event Title *" value="Business Excellence Awards" />
        <Field label="Event Type *" value="Awards Gala" />
        <Field label="Event Code (Optional)" value="BEA2026" />
      </div>
      <div className="mt-6">
        <p className="mb-2 text-sm font-extrabold text-gray-950">Brief Event Description For Hero Banner *</p>
        <RichBox value="Describe the event, its audience, what attendees will gain, keynote speakers, agenda highlights, networking opportunities, and any other important details." />
      </div>
      <div className="mt-6">
        <p className="mb-2 text-sm font-extrabold text-gray-950">Add Event Hero Banner Image to be used as Background Image</p>
        <UploadBox />
      </div>
    </Card>
  );
};

const QuestionComposer = () => (
  <div className="grid gap-7">
    <Card title="Additional Registration Questions" description="Create and edit optional questions to capture key information from attendees.">
      <PixelTable
        columns={["Questions", "Enable Questions", "Actions"]}
        rows={[
          ["Do you have any special accommodations?", "Enabled", "Edit  Delete"],
          ["What accessibility requirements do you have?", "Enabled", "Edit  Delete"],
          ["We are able to offer dietary options.", "Enabled", "Edit  Delete"],
          ["Speaker Question", "Enabled", "Edit"],
        ]}
      />
    </Card>
    <Card title="Submit a Question">
      <Field label="Question" wide />
      <div className="mt-4">
        <GoldButton>Save Question Type</GoldButton>
      </div>
    </Card>
  </div>
);

const CreateStepTwo = ({ variant }: { variant: DashboardPixelQaState }) => (
  <div className="space-y-7">
    <Card title="Date & Location" description="Set the date, time, and location details for your event.">
      <div className="grid grid-cols-3 gap-5">
        <Field label="Start Date" value="June 18, 2026" />
        <Field label="Start Time" value="6:00 AM" />
        <Field label="End Time" value="7:30 PM" />
      </div>
      {variant === "create-step-2-format" ? (
        <div className="mt-7 rounded border border-gray-200 p-5">
          <h3 className="text-base font-extrabold text-gray-950">Select Event Format</h3>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <CheckCard title="In-person" checked />
            <CheckCard title="Virtual" />
            <CheckCard title="Hybrid" />
          </div>
          <div className="mt-5 grid grid-cols-4 gap-4">
            <Field label="Venue Name" value="Sand Bay Convention Centre" />
            <Field label="Address" value="123 Sand Bay Ave" />
            <Field label="City" value="Toronto" />
            <Field label="Province" value="Ontario" />
          </div>
        </div>
      ) : null}
      <div className="mt-7">
        <h3 className="text-base font-extrabold text-gray-950">Create Event Agenda</h3>
        <p className="mt-1 text-sm text-gray-600">Build your event agenda by adding key activities and their start times.</p>
        <PixelTable
          columns={["Start Time", "Details", "Action"]}
          rows={[
            ["8:00 AM", "Registration", "Remove"],
            ["8:30 AM", "Networking", "Remove"],
            ["12:00 PM", "Lunch", "Remove"],
            ["1:00 PM", "Keynote Speaker", "Remove"],
            ["2:00 PM", "Questions", "Remove"],
          ]}
        />
        <div className="mt-4">
          <GoldButton icon={<AddIcon />}>Add Agenda</GoldButton>
        </div>
      </div>
    </Card>
    <div className="grid grid-cols-[1fr_auto] items-end gap-4">
      <Card title="Event Capacity">
        <Field label="Total event capacity" value="240" />
      </Card>
      <WizardActionButton tone="gold">Next Step: Ticket Set Up</WizardActionButton>
    </div>
  </div>
);

const CreateStepThree = ({ modal }: { modal?: "add" | "edit" }) => {
  if (modal) {
    return <TicketModal title={modal === "add" ? "Add Ticket Type" : "Edit Ticket Type"} />;
  }

  return (
    <Card title="Tickets" description="Create ticket types and pricing options for your event.">
      <RadioLine label="This event is free (no tickets required)" />
      <h3 className="mt-7 text-base font-extrabold text-gray-950">Ticket Types</h3>
      <PixelTable
        columns={["Code", "Ticket Type", "Price", "Availability Limit", "Sales Period", "Visible", "Actions"]}
        rows={[
          ["MT", "Member Ticket", "$65.00", "No limit", "May 1, 2026 - Jun 15, 2026", "Yes", "Edit  Delete"],
          ["NMT", "Non-Member Ticket", "$85.00", "No limit", "May 1, 2026 - Jun 15, 2026", "Yes", "Edit  Delete"],
          ["TB", "Table of 8", "$600.00", "12", "May 1, 2026 - Jun 15, 2026", "Yes", "Edit  Delete"],
          ["NCST", "No Charge Sponsorship Ticket", "$0.00", "12", "May 1, 2026 - Jun 15, 2026", "No", "Edit  Delete"],
          ["AST", "Additional Sponsorship Ticket", "$55.00", "No limit", "May 1, 2026 - Jun 15, 2026", "No", "Edit  Delete"],
        ]}
      />
      <div className="mt-5">
        <GoldButton icon={<AddIcon />}>Add Ticket Type</GoldButton>
      </div>
    </Card>
  );
};

const TicketModal = ({ title }: { title: string }) => (
  <div className="grid grid-cols-[1fr_360px] gap-8">
    <Card title={title} description="Add the details for this ticket type.">
      <div className="grid grid-cols-2 gap-5">
        <Field label="Ticket Type Code" />
        <Field label="Ticket Type Name *" />
        <Field label="Ticket Type Description (Optional)" wide />
        <Field label="Price" value="$0.00" />
        <Field label="Sales Period (Optional)" value="Start Date       End Date" />
        <Field label="Availability Limit (Optional)" value="0" />
      </div>
      <div className="mt-6">
        <p className="text-sm font-extrabold text-gray-950">Visible on Registration Form *</p>
        <RadioLine checked label="Yes, show the ticket on the registration form." />
        <RadioLine label="No, hide this ticket from the registration form." />
      </div>
      <div className="mt-6">
        <GoldButton>{title.startsWith("Add") ? "Add Ticket Type" : "Save Ticket Type"}</GoldButton>
      </div>
    </Card>
    <div className="rounded bg-gray-800 p-7 text-white">
      <p className="text-xs uppercase text-gray-300">Screenshot callout</p>
      <p className="mt-4 text-sm leading-6">
        When this option is selected, it should appear as meeting all fixed demo
        criteria for attendees.
      </p>
    </div>
  </div>
);

const CreateStepFour = ({ modal }: { modal?: boolean }) => {
  if (modal) {
    return <SponsorLevelModal />;
  }

  return (
    <Card title="Create Sponsorship Levels" description="Create unique sponsorship packages for this event and include sponsor benefits by clicking on Add Sponsor level.">
      <CheckCard title="Enable sponsorship opportunities for this event" checked />
      <div className="mt-6">
        <GoldButton icon={<AddIcon />}>Add Sponsor Level</GoldButton>
      </div>
      <h3 className="mt-8 text-base font-extrabold text-gray-950">View Created Sponsor Levels</h3>
      <PixelTable
        columns={["Code", "Sponsorship Level", "Tickets Included", "Sponsor Price", "Available Spots", "Total Tickets Allocated", "Action"]}
        rows={[["Code", "Sample Sponsor Level", "0", "$0.00", "0", "0", "Edit  Delete"]]}
      />
      <div className="mt-6 flex justify-between">
        <Button variant="outlined">Previous: Sponsor Set up</Button>
        <WizardActionButton tone="gold">Publish Event</WizardActionButton>
      </div>
    </Card>
  );
};

const SponsorLevelModal = () => (
  <Card title="Create Sponsor Levels" description="Create the sponsor level options for this event.">
    <div className="grid grid-cols-3 gap-4">
      <Field label="Sponsor Level Number *" value="1" />
      <Field label="Sponsor Level Name *" value="Platinum Sponsor" />
      <Field label="Sponsor Level Price *" value="$2,000.00" />
    </div>
    <div className="mt-5 grid grid-cols-4 gap-4">
      <Field label="Code" value="VIP" />
      <Field label="Base Ticket" value="Member Ticket" />
      <Field label="Ticket Qty" value="4" />
      <Field label="Price" value="$0.00" />
    </div>
    <div className="mt-6">
      <h3 className="text-base font-extrabold text-gray-950">Sponsorship Add-on List</h3>
      <PixelTable
        columns={["Code", "Add-on Description", "Quantity", "Action"]}
        rows={[
          ["VIP", "Sample VIP Lounge Event Pass", "2", "Delete"],
          ["SPOT", "Sample Trade Show Booth", "1", "Delete"],
        ]}
      />
    </div>
    <div className="mt-6">
      <p className="mb-2 text-sm font-extrabold text-gray-950">Additional Sponsorship Benefits *</p>
      <RichBox value="Enter sponsorship benefits." />
    </div>
    <div className="mt-5">
      <GoldButton>Save Sponsor</GoldButton>
    </div>
  </Card>
);

const CheckCard = ({ title, checked = false }: { title: string; checked?: boolean }) => (
  <div className={`rounded border p-4 ${checked ? "border-[#E1A731] bg-[#fffaf0]" : "border-gray-200 bg-white"}`}>
    <span className={`mr-2 inline-flex h-4 w-4 rounded ${checked ? "bg-[#E1A731]" : "bg-gray-200"}`} />
    <span className="text-sm font-bold text-gray-950">{title}</span>
  </div>
);

const RadioLine = ({
  label,
  helper,
  checked = false,
}: {
  label: string;
  helper?: string;
  checked?: boolean;
}) => (
  <div className="mt-3 flex items-start gap-3">
    <span className={`mt-1 h-4 w-4 rounded-full border ${checked ? "border-[#E1A731] bg-[#E1A731]" : "border-gray-400"}`} />
    <span>
      <span className="block text-sm font-bold text-gray-950">{label}</span>
      {helper ? <span className="block text-xs text-gray-500">{helper}</span> : null}
    </span>
  </div>
);

const PixelTable = ({ columns, rows }: { columns: string[]; rows: string[][] }) => (
  <div className="mt-4 overflow-hidden rounded border border-gray-200">
    <table className="w-full table-fixed text-left text-xs">
      <thead className="bg-gray-50 text-[11px] font-extrabold uppercase text-gray-500">
        <tr>
          {columns.map((column) => (
            <th key={column} className="border-b border-gray-200 px-3 py-3">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex} className="bg-white">
            {row.map((cell, cellIndex) => (
              <td key={`${rowIndex}-${cellIndex}`} className="px-3 py-3 align-top text-gray-700">
                {cell.includes("Delete") ? (
                  <span className="space-x-2">
                    <span className="rounded bg-[#E1A731] px-2 py-1 text-[10px] font-bold text-white">Edit</span>
                    <span className="rounded bg-red-500 px-2 py-1 text-[10px] font-bold text-white">Delete</span>
                  </span>
                ) : cell === "Enabled" || cell === "Yes" ? (
                  <Pill tone="green">{cell}</Pill>
                ) : cell === "No" ? (
                  <Pill tone="red">{cell}</Pill>
                ) : cell === "Remove" ? (
                  <RedButton>Remove</RedButton>
                ) : (
                  cell
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StatRow = ({ kind }: { kind: "manage" | "ticket" | "sponsor" }) => {
  const cards =
    kind === "manage"
      ? [
          ["Total Events YTD", "43"],
          ["Total Attendees YTD", "2,300"],
          ["Ticket Revenue YTD", "$80,000.00"],
          ["Sponsorship Revenue YTD", "$120,000.00"],
          ["Combined Revenue YTD", "$200,000.00"],
        ]
      : kind === "ticket"
        ? [
            ["Total Tickets Allocated", "40"],
            ["Total Tickets Paid", "20"],
            ["Total Tickets Unpaid", "5"],
            ["Sponsorship Tickets Issued", "2"],
            ["Ticket Revenue", "$1200.00"],
          ]
        : [
            ["Total Sponsors", "8"],
            ["Sponsors Paid", "6"],
            ["Sponsors Unpaid", "2"],
            ["Sponsor Tickets Allocated", "10"],
            ["Revenue", "$17,500.00"],
          ];

  return (
    <div className="grid grid-cols-5 gap-4">
      {cards.map(([label, value]) => (
        <div key={label} className="rounded border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold text-gray-500">{label}</p>
          <p className="mt-2 text-xl font-extrabold text-gray-950">{value}</p>
        </div>
      ))}
    </div>
  );
};

const ManageEvents = ({ actions = false }: { actions?: boolean }) => (
  <div className="space-y-6">
    <div className="flex justify-end">
      <GoldButton icon={<AddIcon />}>Create New Event</GoldButton>
    </div>
    <StatRow kind="manage" />
    <Card title="Manage Event List">
      <div className="mb-4 flex flex-wrap gap-3">
        <Field label="Search" value="Search event name or code" />
        <Field label="Status" value="All statuses" />
        <Field label="Event Type" value="All event types" />
        <GoldButton icon={<FileDownloadOutlinedIcon />}>Export Event List</GoldButton>
      </div>
      <PixelTable
        columns={["Event Name", "Event Date", "Status", "Attendees", "Ticket Revenue", "Sponsor Revenue", "Actions"]}
        rows={[
          ["Business Excellence Awards", "Jun 18, 2026", "Live", "40", "$1,200.00", "$17,500.00", "Edit  Delete"],
          ["Networking Breakfast", "Jul 10, 2026", "Private", "24", "$800.00", "$0.00", "Edit  Delete"],
          ["Annual Golf Tournament", "Aug 20, 2026", "Draft", "0", "$0.00", "$0.00", "Edit  Delete"],
        ]}
      />
    </Card>
    {actions ? <CompositeDialogBoard variant="manage" /> : null}
  </div>
);

const SalesPage = ({ type, table = false }: { type: "ticket" | "sponsor"; table?: boolean }) => (
  <div className="space-y-6">
    <Card title={type === "ticket" ? "Select Event" : "Select Event"}>
      <Field label="Select Event" value="Business Excellence Awards" />
    </Card>
    <StatRow kind={type === "ticket" ? "ticket" : "sponsor"} />
    <Card title={type === "ticket" ? "Create Event Ticket Invoice" : "Create Sponsorship Invoice"}>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Member" value="ABC Insurance Group" />
        <Field label="Billing Contact Name *" value="ABC Insurance Group" />
        <Field label="Email *" value="abc@insurancegroup.com" />
      </div>
      <div className="mt-5">
        <PixelTable
          columns={["Code", "Description", "Qty", "Amount", "Tax", "Total"]}
          rows={[
            [type === "ticket" ? "MT" : "PLAT", type === "ticket" ? "Member Ticket" : "Platinum Sponsorship", type === "ticket" ? "2" : "1", "$65.00", "Tax", type === "ticket" ? "$146.90" : "$2,260.00"],
          ]}
        />
      </div>
      <div className="mt-6">
        <GoldButton>{type === "ticket" ? "Generate Ticket Invoice" : "Generate Sponsorship Invoice"}</GoldButton>
      </div>
    </Card>
    {table ? (
      <Card title={type === "ticket" ? "Ticket Sales List" : "Sponsor Sales List"}>
        <PixelTable
          columns={["Name", "Email", "Phone", "Tickets", "Invoice", "Payment", "Action"]}
          rows={[
            ["Alex Johnson", "alex@example.com", "1234567890", "2", "Generated", "Unpaid", "Edit  Delete"],
            ["Michael Brown", "michael@example.com", "1234567890", "8", "Generated", "Paid", "Edit  Delete"],
          ]}
        />
      </Card>
    ) : null}
  </div>
);

const InvoicePreview = ({ type }: { type: "ticket" | "sponsor" }) => (
  <div className="mx-auto max-w-[760px] bg-[#f4f8ff] p-8">
    <div className="rounded bg-white p-8 shadow-sm">
      <div className="flex justify-between">
        <div>
          <p className="font-extrabold">[Chamber Logo]</p>
          <p className="mt-2 text-sm">Sand Bay Chamber of Commerce</p>
          <p className="text-sm text-gray-500">123 Sand Bay Drive</p>
        </div>
        <div className="text-right">
          <h2 className="text-4xl font-black">INVOICE</h2>
          <p className="mt-2 text-sm">Invoice #: INV-2026-1045</p>
          <Pill tone="blue">Outstanding</Pill>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-8 text-sm">
        <div>
          <p className="font-extrabold">Bill To</p>
          <p>ABC Insurance Group</p>
          <p>Sarah Johnson</p>
        </div>
        <div>
          <p className="font-extrabold">Ship To</p>
          <p>ABC Insurance Group</p>
          <p>Sarah Johnson</p>
        </div>
      </div>
      <PixelTable
        columns={["Code", "Description", "Qty", "Unit Price", "Total"]}
        rows={[[type === "ticket" ? "MT" : "SP", type === "ticket" ? "Member Ticket" : "Platinum Sponsorship", type === "ticket" ? "2" : "1", type === "ticket" ? "$100.00" : "$2,000.00", type === "ticket" ? "$200.00" : "$2,000.00"]]}
      />
      <div className="mt-6 text-right text-xl font-black">TOTAL DUE ${type === "ticket" ? "226.00" : "2,260.00"}</div>
      <PaymentOptions />
    </div>
  </div>
);

const PaymentOptions = () => (
  <div className="mt-8 rounded border border-blue-100 p-4">
    <p className="text-sm font-extrabold text-blue-700">PAYMENT OPTIONS</p>
    <div className="mt-3 grid grid-cols-5 gap-3 text-xs">
      {["Credit Card", "E-Transfer", "Cheque", "Direct Deposit", "Cash"].map((option) => (
        <div key={option} className="rounded border border-blue-100 p-3">
          <p className="font-bold">{option}</p>
          <button className="mt-2 rounded bg-blue-500 px-3 py-1 text-white">Pay Now</button>
        </div>
      ))}
    </div>
  </div>
);

type PaymentDialogState = "none" | "confirm" | "success";

const PaymentPanel = ({
  dialogState = "none",
}: {
  dialogState?: PaymentDialogState;
}) => (
  <div className="mx-auto grid max-w-[980px] gap-8">
    <Card title="Process Payment">
      <p className="text-sm font-bold">Reference Invoice</p>
      <p className="text-sm">INV-2026-1045</p>
      <div className="mt-5">
        <Field label="Remaining Balance Owing" value="$7,260.25" wide />
      </div>
      <PaymentOptions />
      <div className="mt-5 grid grid-cols-2 gap-4">
        <Field label="Payment Date" value="June 18, 2026" />
        <Field label="Amount to Pay" value="$7,260.25" />
      </div>
      <div className="mt-5">
        <Field label="Reference number / cheque # / transaction #" wide />
      </div>
      <div className="mt-6 flex gap-3">
        <button className="flex-1 rounded bg-blue-500 py-3 text-sm font-bold text-white">
          Confirm & Apply Payment
        </button>
        <button className="rounded border border-gray-300 px-5 py-3 text-sm font-bold">Cancel</button>
      </div>
    </Card>
    {dialogState !== "none" ? <PaymentDialogBoard state={dialogState} /> : null}
  </div>
);

const PaymentDialogBoard = ({ state }: { state: Exclude<PaymentDialogState, "none"> }) => (
  <div className="rounded bg-[#3d3d3d] p-10">
    <div className="mx-auto max-w-[380px]">
      {state === "confirm" ? (
        <DialogCard
          icon={<ErrorOutlineIcon fontSize="large" />}
          tone="red"
          title="Are you sure you want to process payment for this invoice?"
          confirm="Proceed"
        />
      ) : (
        <DialogCard
          icon={<CheckCircleIcon fontSize="large" />}
          tone="green"
          title="Your payment has been processed successfully!"
          confirm="Close"
        />
      )}
    </div>
  </div>
);

const CompositeDialogBoard = ({ variant }: { variant: "manage" | "payment" }) => (
  <div className="rounded bg-[#3d3d3d] p-10">
    <div className="mx-auto grid max-w-[760px] grid-cols-2 gap-10">
      <DialogCard
        icon={<ErrorOutlineIcon fontSize="large" />}
        tone="red"
        title={variant === "manage" ? "Are you sure you want to update the status of this event?" : "Are you sure you want to process payment for this invoice?"}
        confirm="Proceed"
      />
      <DialogCard
        icon={<CheckCircleIcon fontSize="large" />}
        tone="green"
        title="Your payment has been processed successfully!"
        confirm="Close"
      />
    </div>
  </div>
);

const DialogCard = ({
  icon,
  tone,
  title,
  confirm,
}: {
  icon: React.ReactNode;
  tone: "red" | "green";
  title: string;
  confirm: string;
}) => (
  <div className="relative rounded bg-white p-8 text-center shadow-xl">
    <CloseIcon className="absolute right-3 top-3 text-red-500" fontSize="small" />
    <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${tone === "red" ? "bg-red-500" : "bg-green-500"} text-white`}>
      {icon}
    </div>
    <p className="mx-auto mt-5 max-w-[280px] text-sm font-extrabold text-gray-950">{title}</p>
    <div className="mt-6 flex justify-center gap-3">
      <button className={`${tone === "red" ? "bg-blue-500" : "bg-gray-950"} rounded px-6 py-2 text-xs font-bold text-white`}>{confirm}</button>
      {tone === "red" ? <BlackButton>Cancel</BlackButton> : null}
    </div>
  </div>
);

const PopupBoard = ({ state }: { state: DashboardPixelQaState }) => {
  const typeMode = state === "event-type-popups";
  const titleMode = state === "event-title-popups";
  const codeMode = state === "event-code-popups";
  return (
    <div className={codeMode ? "grid grid-cols-2 gap-8" : "rounded bg-[#3d3d3d] p-10"}>
      <ModalMini title={codeMode ? "Add Event Code" : typeMode ? "Add Event Type" : "Add Event Title"} action={codeMode ? "Save Code" : typeMode ? "Save Event Type" : "Save Title"} />
      <ModalMini title={codeMode ? "Edit Event Code" : typeMode ? "Edit Event Type" : "Edit Event Title"} action={codeMode ? "Edit Code" : typeMode ? "Edit Event Type" : "Edit Title"} value={titleMode || codeMode || typeMode ? "Business Excellence Awards" : ""} />
      {!codeMode ? (
        <>
          <DialogCard icon={<DeleteOutlineIcon fontSize="large" />} tone="red" title="Are you sure you want to delete the selected item?" confirm="Delete" />
          <DialogCard icon={<CheckCircleIcon fontSize="large" />} tone="green" title="Item deleted successfully!" confirm="Close" />
        </>
      ) : null}
    </div>
  );
};

const ModalMini = ({ title, action, value }: { title: string; action: string; value?: string }) => (
  <div className="rounded bg-white p-6 shadow-xl">
    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
      <h3 className="font-extrabold text-gray-950">{title}</h3>
      <CloseIcon fontSize="small" />
    </div>
    <div className="mt-5">
      <Field label={title} value={value} />
    </div>
    <div className="mt-5 flex justify-end gap-3">
      <button className="text-sm font-bold text-gray-950">Cancel</button>
      <GoldButton>{action}</GoldButton>
    </div>
  </div>
);

const CreateContent = ({ state }: { state: DashboardPixelQaState }) => {
  if (state === "event-code-popups" || state === "event-title-popups" || state === "event-type-popups") {
    return <PopupBoard state={state} />;
  }
  if (state === "create-step-2" || state === "create-step-2-format") {
    return <CreateStepTwo variant={state} />;
  }
  if (state === "add-ticket-popup") {
    return <CreateStepThree modal="add" />;
  }
  if (state === "edit-ticket-popup") {
    return <CreateStepThree modal="edit" />;
  }
  if (state === "create-step-3" || state === "create-step-3-table") {
    return <CreateStepThree />;
  }
  if (state === "sponsor-level-popup") {
    return <CreateStepFour modal />;
  }
  if (state === "create-step-4") {
    return <CreateStepFour />;
  }
  return <CreateStepOne variant={state} />;
};

const isCreateState = (state: DashboardPixelQaState) =>
  activeStepByState[state] !== undefined;

const titleForState = (state: DashboardPixelQaState) => {
  if (state.startsWith("ticket")) return "Invoice and Manage Event Ticket Sales";
  if (state.startsWith("sponsor") || state.startsWith("sponsorship")) {
    return "Invoice and Manage Sponsorship Sales";
  }
  if (state.startsWith("manage")) return "Manage Events";
  const step = activeStepByState[state] || 1;
  return createTitleByStep[step];
};

const descriptionForState = (state: DashboardPixelQaState) => {
  if (state.startsWith("ticket")) return "Manually set up event invoice and manage all tickets sales for events.";
  if (state.startsWith("sponsor") || state.startsWith("sponsorship")) {
    return "Manually invoice event sponsorships and manage sponsor sales.";
  }
  if (state.startsWith("manage")) return "Track your event calendar and filter events for a full list.";
  const step = activeStepByState[state] || 1;
  return createDescriptionByStep[step];
};

const HeaderActions = ({ state }: { state: DashboardPixelQaState }) => {
  if (!isCreateState(state)) {
    return state.startsWith("manage") ? <GoldButton icon={<AddIcon />}>Create New Event</GoldButton> : null;
  }
  return (
    <div className="flex gap-3">
      <WizardActionButton tone="gold" startIcon={<SaveOutlinedIcon />}>Save as Draft</WizardActionButton>
      {(activeStepByState[state] || 1) === 4 ? (
        <WizardActionButton tone="green" startIcon={<CalendarMonthIcon />}>Schedule to Publish</WizardActionButton>
      ) : null}
    </div>
  );
};

export const EventDashboardPixelQa = ({
  state,
}: {
  state: DashboardPixelQaState;
}): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();

  useEffect(() => {
    setPageHeaderOverride({
      title: titleForState(state),
      description: descriptionForState(state),
      titleClassName: "text-[34px] font-extrabold leading-tight text-black",
      descriptionClassName: "mt-3 text-lg leading-7 text-gray-500 whitespace-pre-line",
      actions: <HeaderActions state={state} />,
    });
    return () => setPageHeaderOverride(null);
  }, [setPageHeaderOverride, state]);

  let content: React.ReactNode;
  if (isCreateState(state)) {
    const activeStep = activeStepByState[state] || 1;
    content = (
      <>
        <Stepper activeStep={activeStep} />
        <CreateContent state={state} />
      </>
    );
  } else if (state === "manage-events" || state === "manage-actions") {
    content = <ManageEvents actions={state === "manage-actions"} />;
  } else if (state === "ticket-invoice") {
    content = <InvoicePreview type="ticket" />;
  } else if (state === "sponsor-invoice") {
    content = <InvoicePreview type="sponsor" />;
  } else if (
    state === "ticket-payment" ||
    state === "sponsor-payment" ||
    state === "ticket-payment-confirm" ||
    state === "sponsor-payment-confirm" ||
    state === "ticket-payment-success" ||
    state === "sponsor-payment-success"
  ) {
    const dialogState: PaymentDialogState = state.endsWith("-confirm")
      ? "confirm"
      : state.endsWith("-success")
        ? "success"
        : "none";
    content = <PaymentPanel dialogState={dialogState} />;
  } else if (state === "sponsorship-sales" || state === "sponsorship-sales-table") {
    content = <SalesPage type="sponsor" table={state === "sponsorship-sales-table"} />;
  } else {
    content = <SalesPage type="ticket" table={state === "ticket-sales-table"} />;
  }

  return <div className="w-full pb-12">{content}</div>;
};

export default EventDashboardPixelQa;
